import { StageDef } from '../automationMachineInterfaces'
import { TWITTER_AUTOMATION_STAGE_STATUSES } from './twitterAutomationStageStatuses'
import { locationInformationUrl } from './twitterUrls'
import { XSettingKey } from '../../recommendations/recommendationSettingsKeys'
import * as enums from '../../utils/enums'
import { TwitterRecommendationKey } from '../../utils/enums'
import {
  getDomPromise,
  getWaitForElementToAppearPromise,
} from '../interruptiblePromises'
import { evaluateXpath } from '../interruptibleXpath'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'

// Settings property functions

const currentValueFns = {
  [XSettingKey.TWEET_LOCATION_FUTURE]: getCurrentValueFnFromKey(
    XSettingKey.TWEET_LOCATION_FUTURE,
    (value) => {
      if (value === null || value === undefined) {
        return 'Not collected'
      }

      return value ? 'Enabled' : 'Disabled'
    },
  ),
  [XSettingKey.TWEET_LOCATION_EXISTING]: () => null,
}

const cantUndoFns = {
  [XSettingKey.TWEET_LOCATION_FUTURE]: defaultCantUndoFn,
  [XSettingKey.TWEET_LOCATION_EXISTING]: () => true,
}

const getCurrentValueFn = (key) => currentValueFns[key]

const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) => !value)

const getCantUndoFn = (key) => cantUndoFns[key]

const getIsDisabledFn = () => defaultIsDisabledFn

const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

// "Remove all location information from posts" automation utils

const _REMOVE_ALL_LABEL_TEXT =
  'Remove all location information attached to your posts'
const _CONFIRMATION_MODAL_CONFIRM_TEXT = 'Delete'
const _CONFIRMATION_MODAL_CANCEL_TEXT = 'Cancel'

function getRemoveAllButton(contextNode: Node): HTMLElement {
  return evaluateXpath<HTMLElement>(
    contextNode,
    `descendant::*[text() = "${_REMOVE_ALL_LABEL_TEXT}"]`,
  )[0]
}

function waitForConfirmationModal(eventType): Promise<Node> {
  const _checkNode = (node) =>
    evaluateXpath(
      node,
      `self::*[` +
        `  count(./descendant::*[text() = "${_CONFIRMATION_MODAL_CONFIRM_TEXT}"]) > 0 and` +
        `  count(./descendant::*[text() = "${_CONFIRMATION_MODAL_CANCEL_TEXT}"]) > 0` +
        `]`,
    )[0] !== undefined

  const defaultPromise: Promise<Node> = new Promise((resolve) => {
    resolve(document)
  })
  if (eventType === 'OPEN') {
    if (_checkNode(document)) {
      return defaultPromise
    }
  } else if (eventType === 'CLOSE') {
    if (!_checkNode(document)) {
      return defaultPromise
    }
  } else {
    throw Error(`Did not recognize eventType ${eventType}`)
  }

  return getDomPromise(document, _checkNode)
}

function getConfirmationModalButton(
  contextNode: Node,
  buttonType: string,
): HTMLElement {
  let pattern
  if (buttonType === 'CONFIRM') {
    pattern = `descendant::*[text() = "${_CONFIRMATION_MODAL_CONFIRM_TEXT}"]`
  } else if (buttonType === 'CANCEL') {
    pattern = `descendant::*[text() = "${_CONFIRMATION_MODAL_CANCEL_TEXT}"]`
  } else {
    throw Error(`Did not recognize buttonType ${buttonType}`)
  }

  return evaluateXpath<HTMLElement>(contextNode, pattern)[0]
}

// "Add location" flag automation utils

async function getAddLocationFlagCheckbox(): Promise<HTMLInputElement> {
  const optionLabel = await getWaitForElementToAppearPromise<HTMLElement>(
    document,
    () =>
      evaluateXpath<HTMLElement>(
        document,
        '//body/descendant-or-self::*[' +
          "  contains(text(), 'Add location information to your') and" +
          "  count(./ancestor::*[@role = 'heading']) = 0" +
          ']',
      )[0],
  )

  if (!optionLabel) {
    throw Error('Could not find label for option')
  }

  const optionCheckbox = evaluateXpath<HTMLInputElement>(
    optionLabel,
    '../../descendant::input',
  )[0]

  if (!optionCheckbox) {
    throw Error('Could not find checkbox for option')
  }

  return optionCheckbox
}

async function gatherAddLocationFlag(): Promise<boolean> {
  const optionCheckbox = await getAddLocationFlagCheckbox()
  return optionCheckbox.checked
}

async function toggleAddLocationFlag(): Promise<void> {
  const optionCheckbox = await getAddLocationFlagCheckbox()

  if (!optionCheckbox) {
    throw new Error('Could not find checkbox element for add location flag')
  }
  optionCheckbox.click()
}

// Setup gather stage definitions

enum StageKey {
  GATHER_SETTING_TWEETS_LOCATION = 'GATHER_SETTING_TWEETS_LOCATION',
  UPDATE_SETTING_TWEETS_LOCATION = 'UPDATE_SETTING_TWEETS_LOCATION',
  UNDO_SETTING_TWEETS_LOCATION = 'UNDO_SETTING_TWEETS_LOCATION',
}

const gatherStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_TWEETS_LOCATION,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: locationInformationUrl,
    action: async (machineContext, userContext) => {
      const { updateStoredInitialValues } = userContext

      const flag = await gatherAddLocationFlag()
      await updateStoredInitialValues(
        { [XSettingKey.TWEET_LOCATION_FUTURE]: flag },
        TwitterRecommendationKey.TWITTER_LOCATION_TWEETS,
      )
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_TWEETS_LOCATION,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: locationInformationUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, isDry } = userContext

      // Clear tweets locations
      if (
        settingsSelected &&
        settingsSelected[XSettingKey.TWEET_LOCATION_EXISTING]
      ) {
        const addedNode = await getWaitForElementToAppearPromise(
          document,
          (node) => getRemoveAllButton(node),
        )

        const waitForModalOpen = waitForConfirmationModal('OPEN')

        if (!addedNode) {
          throw new Error('Could not find element for remove all button')
        }
        addedNode.click()
        const modalNode = await waitForModalOpen

        const button = await getConfirmationModalButton(
          modalNode,
          isDry ? 'CANCEL' : 'CONFIRM',
        )

        const waitForModalClose = waitForConfirmationModal('CLOSE')

        if (!button) {
          throw new Error('Could not find cancel button')
        }
        button.click()
        await waitForModalClose
      }

      // Set flag
      if (
        settingsSelected &&
        settingsSelected[XSettingKey.TWEET_LOCATION_FUTURE]
      ) {
        const currentFlagValue = await gatherAddLocationFlag()

        if (currentFlagValue && !isDry) {
          await toggleAddLocationFlag()
        }
      }

      return null
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_TWEETS_LOCATION,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: locationInformationUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, isDry } = userContext

      // Skip if not selected
      if (
        settingsSelected &&
        settingsSelected[XSettingKey.TWEET_LOCATION_FUTURE]
      ) {
        const currentFlagValue = await gatherAddLocationFlag()
        if (!currentFlagValue && !isDry) {
          await toggleAddLocationFlag()
        }
      }

      return null
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
